import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { userAPI, authAPI } from '../../services/api';
import toast from 'react-hot-toast';
import TextInput from '../Common/TextInput';
import LoadingSpinner from '../Common/LoadingSpinner';
import ConfirmationModal from '../Common/ConfirmationModal';
import ViewProfileSelf from './ViewProfileSelf';
import { essApi } from '../../services/hrmApi';
import { getFileUrl } from '../../utils/fileUtils';
import SecureImage from '../Common/SecureImage';

const UserProfile = () => {
  const { user, updateUser, changePassword, logout, getToken } = useAuth();
  const { roles } = useRBAC();

  const [activeTab, setActiveTab] = useState('view');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [employee, setEmployee] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Personal Information Form
  const [personalForm, setPersonalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: ''
  });

  // Preferences Form
  const [preferencesForm, setPreferencesForm] = useState({
    language: 'en',
    theme: 'light',
    notifications: {
      email: true,
      push: true
    }
  });

  // Password Change Form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (user) {
      setPersonalForm({
        firstName: user.firstName || user.profile?.firstName || '',
        lastName: user.lastName || user.profile?.lastName || '',
        email: user.email || '',
        phone: user.phone || user.profile?.phone || '',
        department: user.department || user.profile?.department || ''
      });

      // Initialize preferences if available
      if (user.preferences) {
        setPreferencesForm({
          language: user.preferences.language || 'en',
          theme: user.preferences.theme || 'light',
          notifications: {
            email: user.preferences.notifications?.email === true,
            push: user.preferences.notifications?.push === true
          }
        });
      }
    }
  }, [user]);

  // Fetch linked employee profile (contains profilePhoto / pendingProfilePhoto)
  const fetchEmployeeProfile = async () => {
    try {
      const response = await essApi.getViewProfileSelf();
      if (response.data?.success) {
        setEmployee(response.data.data);
      }
    } catch (error) {
      console.warn('Failed to load employee profile for photo management:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchEmployeeProfile();
    }
  }, [user]);

  // Handle Profile Photo Upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, JPEG, WEBP)');
      return;
    }

    const formData = new FormData();
    formData.append('photo', file);

    try {
      setUploadingPhoto(true);
      toast.loading('Uploading profile photo...', { id: 'profile-photo-upload' });

      const response = await essApi.updateProfilePhoto(formData);
      if (response.data?.success) {
        toast.success(response.data.message || 'Profile photo uploaded. Awaiting admin approval.', { id: 'profile-photo-upload' });
        await fetchEmployeeProfile();
      } else {
        toast.error(response.data.message || 'Failed to upload photo', { id: 'profile-photo-upload' });
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error(error.response?.data?.message || 'Failed to upload profile photo', { id: 'profile-photo-upload' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle personal information form changes
  const handlePersonalChange = (field, value) => {
    setPersonalForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle password form changes
  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate personal information form
  const validatePersonalForm = () => {
    const newErrors = {};

    if (!personalForm.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!personalForm.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (personalForm.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(personalForm.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate password form
  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    }

    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle personal information update
  const handlePersonalSubmit = async (e) => {
    e.preventDefault();

    if (!validatePersonalForm()) {
      return;
    }

    setLoading(true);

    try {
      const { email, ...updateData } = personalForm;
      const response = await userAPI.updateProfile(updateData);

      if (response.data.success) {
        // Update user context
        const updatedUser = response.data.data.user;
        updateUser(updatedUser);

        toast.success('Profile updated successfully');
        setErrors({});
      } else {
        toast.error(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);

      if (error.response?.data?.errors) {
        const fieldErrors = {};
        error.response.data.errors.forEach(err => {
          if (err.includes('firstName')) fieldErrors.firstName = err;
          if (err.includes('lastName')) fieldErrors.lastName = err;
          if (err.includes('email')) fieldErrors.email = err;
          if (err.includes('phone')) fieldErrors.phone = err;
          if (err.includes('department')) fieldErrors.department = err;
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmNewPassword: passwordForm.confirmPassword
      });

      if (result.success) {
        toast.success('Password changed successfully. You will be logged out for security.', {
          duration: 3000
        });
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordChange(false);
        setErrors({});

        // Log out the user after a short delay to show the success message
        setTimeout(() => {
          logout();
        }, 3000);
      } else {
        toast.error(result.error || 'Failed to change password');

        // Handle validation errors from API response
        if (result.response?.data?.errors) {
          const fieldErrors = {};
          result.response.data.errors.forEach(err => {
            if (err.field === 'currentPassword') {
              fieldErrors.currentPassword = err.message;
            } else if (err.field === 'newPassword') {
              fieldErrors.newPassword = err.message;
            } else if (err.field === 'confirmNewPassword') {
              fieldErrors.confirmPassword = err.message;
            }
          });
          setErrors(fieldErrors);
        }
      }
    } catch (error) {
      console.error('Password change error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Handle push notification toggle (just updates local state)
  const handlePushNotificationToggle = () => {
    setPreferencesForm(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        push: !prev.notifications.push
      }
    }));
  };

  // Handle preferences submit
  const handlePreferencesSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build preferences object
      const updatedPreferences = {
        language: preferencesForm.language,
        theme: preferencesForm.theme,
        notifications: {
          email: preferencesForm.notifications.email,
          push: preferencesForm.notifications.push
        }
      };

      const response = await userAPI.updateProfile({
        preferences: updatedPreferences
      });

      if (response.data.success) {
        updateUser(response.data.data.user);
        toast.success('Preferences updated successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  // Get user role information
  const getUserRoleInfo = () => {
    if (!user || !user.roles || !Array.isArray(user.roles)) {
      return { primaryRole: 'User', allRoles: [] };
    }

    const primaryRole = user.roles.find(role => role.isPrimary) || user.roles[0];
    return {
      primaryRole: primaryRole?.name || primaryRole?.code || 'User',
      allRoles: user.roles.map(role => role.name || role.code).join(', ')
    };
  };

  // Get user status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  const roleInfo = getUserRoleInfo();

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-center space-x-4">
            {/* Avatar & Upload Option */}
            <div className="relative group flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center overflow-hidden border-2 border-white/50 relative">
                <SecureImage
                  src={employee?.pendingProfilePhoto || employee?.profilePhoto}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  fallback={
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  }
                />
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              <label className="absolute -bottom-1 -right-1 bg-white hover:bg-gray-100 text-gray-700 p-1.5 rounded-full shadow-md cursor-pointer border border-gray-200 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
              </label>
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold text-white">
                  {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
                </h1>
                {employee?.pendingProfilePhoto && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                    Photo Pending Approval
                  </span>
                )}
              </div>
              <p className="text-indigo-100 text-sm mt-0.5">
                {user.email}
              </p>
              <div className="flex items-center space-x-3 mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(user.status)}`}>
                  {user.status || 'Active'}
                </span>
                <span className="text-indigo-100 text-xs">
                  {roleInfo.primaryRole}
                </span>
                {user.emailVerified && (
                  <span className="inline-flex items-center text-indigo-100 text-xs">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Verified
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-6 px-4">
            <button
              onClick={() => setActiveTab('view')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'view'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              View Profile
            </button>
            <button
              onClick={() => setActiveTab('personal')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'personal'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Personal
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'security'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Security
            </button>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'permissions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Permissions
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'preferences'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Preferences
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-0">
          {/* View Profile Tab */}
          {activeTab === 'view' && <ViewProfileSelf />}

          {/* Personal Information Tab */}
          {activeTab === 'personal' && (
            <div className="p-6 space-y-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Personal Information</h3>

              <form onSubmit={handlePersonalSubmit} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextInput
                    label="First Name"
                    variant="input"
                    value={personalForm.firstName}
                    onChange={(e) => handlePersonalChange('firstName', e.target.value)}
                    error={errors.firstName}
                    required
                    placeholder="Enter your first name"
                    disabled={loading}
                  />

                  <TextInput
                    label="Last Name"
                    variant="input"
                    value={personalForm.lastName}
                    onChange={(e) => handlePersonalChange('lastName', e.target.value)}
                    error={errors.lastName}
                    required
                    placeholder="Enter your last name"
                    disabled={loading}
                  />
                </div>

                <TextInput
                  label="Email Address"
                  variant="input"
                  type="email"
                  value={personalForm.email}
                  onChange={() => { }} // Email is read-only for security
                  error={errors.email}
                  required
                  placeholder="Contact administrator to change email"
                  disabled={true}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextInput
                    label="Phone Number"
                    variant="input"
                    type="tel"
                    value={personalForm.phone}
                    onChange={(e) => handlePersonalChange('phone', e.target.value)}
                    error={errors.phone}
                    placeholder="Enter your phone number"
                    disabled={loading}
                  />

                  <TextInput
                    label="Department"
                    variant="input"
                    value={personalForm.department}
                    onChange={(e) => handlePersonalChange('department', e.target.value)}
                    error={errors.department}
                    placeholder="Enter your department"
                    disabled={loading}
                  />
                </div>

                <div className="flex justify-end pt-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Security & Privacy Tab */}
          {activeTab === 'security' && (
            <div className="p-6 space-y-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Security & Privacy</h3>

              {/* Password Section */}
              <div className="bg-gray-50 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Password</h4>
                    <p className="text-xs text-gray-600">Keep your account secure</p>
                  </div>
                  <button
                    onClick={() => setShowPasswordChange(true)}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors text-sm"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Account Info */}
              <div className="bg-gray-50 rounded-md p-3">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Account Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Created:</span>
                      <span className="text-xs text-gray-900">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Updated:</span>
                      <span className="text-xs text-gray-900">
                        {user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Last Login:</span>
                      <span className="text-xs text-gray-900">
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Email:</span>
                      <span className={`text-xs flex items-center ${user.emailVerified ? 'text-green-600' : 'text-red-600'}`}>
                        {user.emailVerified ? (
                          <>
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Not Verified
                          </>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Status:</span>
                      <span className={`text-xs font-medium ${user.status === 'active' ? 'text-green-600' :
                        user.status === 'inactive' ? 'text-gray-600' : 'text-red-600'
                        }`}>
                        {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">ID:</span>
                      <span className="text-xs text-gray-900 font-mono">
                        {user._id ? `...${user._id.slice(-8)}` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Roles & Permissions Tab */}
          {activeTab === 'permissions' && (
            <div className="p-6 space-y-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Roles & Permissions</h3>

              {/* Current Roles */}
              <div className="bg-gray-50 rounded-md p-3">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Current Roles</h4>
                <div className="space-y-2">
                  {user.roles && user.roles.length > 0 ? (
                    user.roles.map((role, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-white rounded-md border">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${role.isPrimary ? 'bg-indigo-500' : 'bg-gray-400'}`}></div>
                          <div>
                            <span className="text-sm font-medium text-gray-900">{role.roleCode}</span>
                            {role.isPrimary && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                Primary
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            {new Date(role.assignedAt).toLocaleDateString()}
                          </div>
                          {role.assignedBy && (
                            <div className="text-xs text-gray-400">
                              {role.assignedBy.name}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No roles assigned</p>
                  )}
                </div>
              </div>

              {/* Permission Summary */}
              {user.permissionCount && (
                <div className="bg-gray-50 rounded-md p-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Permission Summary</h4>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="text-center">
                      <div className="text-lg font-bold text-indigo-600">{user.permissionCount}</div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">
                        {user.effectivePermissions ? user.effectivePermissions.length : 0}
                      </div>
                      <div className="text-xs text-gray-500">Effective</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">
                        {user.roles ? user.roles.length : 0}
                      </div>
                      <div className="text-xs text-gray-500">Roles</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-bold text-purple-600">
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </div>
                      <div className="text-xs text-gray-500">Last Login</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Permissions by Category */}
              {user.permissions && user.permissions.length > 0 && (
                <div className="bg-gray-50 rounded-md p-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Permissions by Category</h4>
                  {(() => {
                    const categories = {};
                    user.permissions.forEach(permission => {
                      const category = permission.split(':')[0];
                      if (!categories[category]) categories[category] = [];
                      categories[category].push(permission);
                    });

                    return (
                      <div className="space-y-3">
                        {Object.entries(categories).map(([category, perms]) => (
                          <div key={category} className="bg-white rounded-md p-2 border">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="text-sm font-medium text-gray-900">{category}</h5>
                              <span className="text-xs text-gray-500">{perms.length}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {perms.map((permission, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {permission.split(':').slice(1).join(':')}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="p-6 space-y-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Preferences</h3>

              {/* Language & Theme */}
              <div className="bg-gray-50 rounded-md p-3">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Display Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                    <select
                      value={preferencesForm.language}
                      onChange={(e) => setPreferencesForm(prev => ({ ...prev, language: e.target.value }))}
                      className="w-full border-0 bg-white rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                      disabled={true}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                    <select
                      value={preferencesForm.theme}
                      onChange={(e) => setPreferencesForm(prev => ({ ...prev, theme: e.target.value }))}
                      className="w-full border-0 bg-white rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                      disabled={true}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="bg-gray-50 rounded-md p-3">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Notification Settings</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-900">Email Notifications</span>
                      <p className="text-xs text-gray-500">Receive notifications via email</p>
                    </div>
                    <button
                      type="button"
                      disabled={true}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${preferencesForm.notifications.email ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${preferencesForm.notifications.email ? 'translate-x-5' : 'translate-x-0'
                          }`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-900">Push Notifications</span>
                      <p className="text-xs text-gray-500">Receive push notifications in browser</p>
                    </div>
                    <button
                      type="button"
                      onClick={handlePushNotificationToggle}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${preferencesForm.notifications.push ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${preferencesForm.notifications.push ? 'translate-x-5' : 'translate-x-0'
                          }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-3">
                  <button
                    onClick={handlePreferencesSubmit}
                    disabled={loading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
          <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-100">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Change Password</h3>
                <button
                  onClick={() => setShowPasswordChange(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handlePasswordSubmit} className="p-4 space-y-4">
              <TextInput
                label="Current Password"
                type="password"
                variant="input"
                value={passwordForm.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                error={errors.currentPassword}
                required
                placeholder="Enter current password"
                disabled={loading}
              />

              <TextInput
                label="New Password"
                type="password"
                variant="input"
                value={passwordForm.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                error={errors.newPassword}
                required
                placeholder="Min 8 characters"
                disabled={loading}
              />

              <TextInput
                label="Confirm New Password"
                type="password"
                variant="input"
                value={passwordForm.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                error={errors.confirmPassword}
                required
                placeholder="Confirm new password"
                disabled={loading}
              />

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPasswordChange(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm"
                >
                  {loading ? 'Changing...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfile;